import Close from '@mui/icons-material/Close';
import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import { useWindowWidth } from '@react-hook/window-size';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighChartsMore from 'highcharts/highcharts-more';
import React from 'react';
import { ChartRanking, homeStyles } from '..';
import { AnimateIn } from '../../../components/animate-in';
import { uicolors } from '../../../theme';
import { Chart } from './chart';

HighChartsMore(Highcharts);

export function MobileChart(props: {
  AgtNo: string;
  Title: string;
  data: ChartRanking[];
  ToolTip?: string | null;
  isCurrency?: boolean;
  cantView?: boolean;
}) {
  // Props
  const { AgtNo, Title, data, isCurrency, cantView } = props;
  // Hooks
  const width = useWindowWidth();
  // State
  const [open, setOpen] = React.useState<string | null>(null);

  const chart = {
    chart: {
      type: 'column',
      backgroundColor: '#fff',
      height: 125,
      width: width < 400 ? width * 0.3 : width * 0.35,
    },
    plotOptions: {
      column: {
        minPointLength: 3,
      },
    },
    title: {
      text: Title,
      align: 'center',
      style: {
        color: uicolors.gold[500],
        fontWeight: 'bold',
        fontSize: 12,
        fontFamily: 'Lato',
        letterSpacing: 1,
      },
    },
    xAxis: {
      type: 'category',
      lineWidth: 0,
      labels: {
        enabled: false,
      },
    },
    yAxis: {
      gridLineWidth: 0,
      title: {
        text: '',
      },
      labels: {
        enabled: false,
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    series: [
      {
        groupPadding: 0,
        borderWidth: 0,
        borderRadius: 5,
        data: data.map((i) => {
          const isHighlighted = i.AgtNo === AgtNo; // Check if AgtNo matches
          return {
            name: i.AgtName,
            y: i.Total,
            color: isHighlighted
              ? {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Highlight color
                  stops: [
                    [0, uicolors.gold[100]],
                    [0.5, uicolors.gold[500]],
                    [1, uicolors.gold[900]],
                  ],
                }
              : {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Default color
                  stops: [
                    [0, '#00BBFF'],
                    [1, '#00527A'],
                  ],
                },
          };
        }),
      },
    ],

    credits: {
      enabled: false,
    },
  };

  return (
    <Stack
      pb={2}
      direction="row"
      justifyContent="space-evenly"
      sx={{
        flexWrap: 'wrap',
        gap: 2,
        filter: cantView ? 'blur(10px)' : '',
        pointerEvents: cantView ? 'none' : 'auto',
        userSelect: cantView ? 'none' : 'auto',
      }}
    >
      <Box
        component="button"
        aria-label="enhance chart"
        p={1}
        sx={{
          ...homeStyles.box,
          letterSpacing: 1,
          backgroundColor: '#fff',
          border: `1px solid ${uicolors.blueNav[950]}90`,
          boxShadow: '1px 1px 3px grey',
        }}
        onClick={() => {
          open === 'graph' ? setOpen(null) : setOpen('graph');
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={chart} />
      </Box>
      <Box
        component="button"
        aria-label="enhance list"
        px={0}
        sx={{
          ...homeStyles.box,
          backgroundColor: '#fff',
          border: `1px solid ${uicolors.blueNav[950]}90`,
          boxShadow: '1px 1px 3px grey',
          letterSpacing: 1,
          display: 'flex',
          alignItems: 'stretch',
          flexDirection: 'column',
          justifyContent: 'space-around',
          width: width * 0.36,
          fontSize: 11,
          textTransform: 'none',
          py: 1,
        }}
        onClick={() => {
          open === 'list' ? setOpen(null) : setOpen('list');
        }}
      >
        {data.map((i) => {
          return (
            <Stack
              key={i.AgtName}
              px={1}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              sx={{
                color:
                  i.AgtNo === AgtNo
                    ? uicolors.gold[500]
                    : uicolors.blueNav[950],
                fontWeight: i.AgtNo === AgtNo ? 700 : 400,
              }}
            >
              <Tooltip
                title={i.AgtName}
                arrow
                placement="left"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: uicolors.blueNav[950],
                      color: '#fff',
                    },
                  },
                  arrow: { sx: { color: uicolors.blueNav[950] } },
                }}
              >
                <Box
                  px={1}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {i.AgtName}
                </Box>
              </Tooltip>
              <Box px={1}>{Math.floor(i.Total || 0).toLocaleString()}</Box>
            </Stack>
          );
        })}
      </Box>
      {open !== null && (
        <AnimateIn>
          <Box p={1} sx={{ textAlign: 'center' }}>
            <Chart
              AgtNo={AgtNo}
              Title={Title}
              data={data}
              ToolTip={props.ToolTip || ''}
              isCurrency={isCurrency}
              mobileOpen={open}
            />

            <IconButton
              aria-label="close enhanced chart"
              size="small"
              sx={{
                mt: 3,
                borderRadius: 5,
                borderColor: uicolors.blueNav[500],
              }}
              onClick={() => setOpen(null)}
            >
              <Close fontSize="small" sx={{ color: uicolors.blueNav[500] }} />
            </IconButton>

            <Divider
              sx={{
                mt: 2,
                borderBottomWidth: 1,
                backgroundColor: uicolors.blueNav[50],
              }}
            />
          </Box>
        </AnimateIn>
      )}
    </Stack>
  );
}
